import { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react'
import { api, type PermissionKey } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'

const MANAGER_ROLES = new Set(['owner', 'admin', 'platform_admin'])

type PermissionsContextType = {
  role: string | null
  isManager: boolean
  isLoading: boolean
  can: (key: PermissionKey) => boolean
  refresh: () => Promise<void>
}

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined)

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, user } = useAuth()
  const [role, setRole] = useState<string | null>(null)
  const [permissions, setPermissions] = useState<Partial<Record<PermissionKey, boolean>> | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const tenantId = user?.tenantId ?? null

  const refresh = useCallback(async () => {
    if (!isAuthenticated || !tenantId) {
      setRole(null)
      setPermissions(null)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    try {
      const result = await api.getMyPermissions()
      setRole(result.role)
      setPermissions(result.permissions)
    } catch (error) {
      console.error('Failed to load permissions:', error)
      setRole(user?.role ?? null)
      setPermissions(null)
    } finally {
      setIsLoading(false)
    }
  }, [isAuthenticated, tenantId, user?.role])

  useEffect(() => {
    refresh()
  }, [refresh])

  const effectiveRole = role ?? user?.role ?? null
  const isManager = MANAGER_ROLES.has(effectiveRole ?? '')

  const can = useCallback(
    (key: PermissionKey) => {
      if (permissions) return Boolean(permissions[key])
      return isManager
    },
    [permissions, isManager],
  )

  const value = useMemo(
    () => ({ role: effectiveRole, isManager, isLoading, can, refresh }),
    [effectiveRole, isManager, isLoading, can, refresh],
  )

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>
}

export function usePermissions() {
  const context = useContext(PermissionsContext)
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionsProvider')
  }
  return context
}
